import { DateTime, Interval } from 'luxon';
import { type DeviceType, currentDeviceType } from '@/helpers/deviceType';
import type { UserDetails } from '@/stores/user';

const intercomAppId = import.meta.env.VITE_INTERCOM_APP_ID;

type IntercomBootOptions = {
	/**
	 * Only pass this when the account was created in this session. Intercom keeps attributes it is
	 * not sent, so leaving it out on every later boot preserves the device captured at sign up.
	 */
	signedUpWithDeviceType?: DeviceType;
	/**
	 * Overrides the source stored on the user. Needed on the sign-up page, which boots Intercom for
	 * an anonymous visitor before an account exists, so the source is only known from the URL.
	 */
	signUpSource?: string | null;
};

/**
 * Boots the Intercom messenger. Kept in one place so every entry point into the dashboard — the
 * sign-up and login pages, email/password sign up, each social login, and the router guard — sends
 * Intercom the same set of user attributes.
 */
export const bootIntercom = (userDetails: UserDetails | null, options: IntercomBootOptions = {}): void => {
	const planDetails = userDetails?.plan ?? null;
	const activationDateString = planDetails?.activationDate ?? null;
	const expiryDateString = planDetails?.expiryDate ?? null;
	const startDate = activationDateString ? DateTime.fromISO(activationDateString) : null;
	const startDateFormatted = startDate ? startDate.toFormat('dd MMM yyyy') : null;
	const endDate = expiryDateString ? DateTime.fromISO(expiryDateString) : null;
	const endDateFormatted = endDate ? endDate.toFormat('dd MMM yyyy') : null;
	const duration = !!startDate && !!endDate ? Interval.fromDateTimes(startDate, endDate).count('days') + 1 : null;

	window.Intercom('boot', {
		api_base: 'https://api-iam.intercom.io',
		app_id: intercomAppId,
		custom_launcher_selector: '#buttonShowIntercom',
		hide_default_launcher: true,
		name: userDetails?.name,
		email: userDetails?.email,
		type: userDetails?.type,
		user_id: userDetails?.id,
		created_at: userDetails?.timestamps?.createdAt,
		sign_up_source: options.signUpSource ?? userDetails?.signUpSource,
		email_verification_status: userDetails?.emailVerificationStatus,
		plan_details_type: planDetails?.type,
		plan_details_activationDate: activationDateString,
		plan_details_activationDateFormatted: startDateFormatted,
		plan_details_expiryDate: expiryDateString,
		plan_details_expiryDateFormatted: endDateFormatted,
		plan_details_duration: duration,
		current_device_type: currentDeviceType(),
		...(options.signedUpWithDeviceType ? { signed_up_with_device_type: options.signedUpWithDeviceType } : {}),
	});
};
